import React from "react";
import { Avatar, Box, Typography } from "@mui/material";
import moment from "moment";
import inkfreeLogo from "assets/images/side_nav_inkfree_logo.svg";
import { BrandingSettings } from "../BrandingService";
import { getRenderableLogoUrl } from "./LogoUpload";
import brandingStyles from "../Branding.styles";

const styles = brandingStyles;

interface EmailPreviewProps {
  settings: BrandingSettings;
  /** Requester shown in the mock email — the signed-in user. */
  senderName: string;
}

/** Product name the branded mail is sent "via"; never white-labelled. */
export const PRODUCT_NAME = "Inkfree";
const SAMPLE_DOCUMENT = "Service Agreement.pdf";
const EXPIRY_FORMAT = "DD MMM YYYY";
const SAMPLE_URL =
  "https://sign.inkfree.app/document/sign/do?id=<request-id>&key=<token>";

/**
 * Mock signing email rendered inside the sticky preview panel. Mirrors the
 * Branding design's "Live Email Preview" 1:1 and re-renders as the brand name,
 * logo, colour and footer change.
 */
const EmailPreview: React.FC<EmailPreviewProps> = ({
  settings,
  senderName,
}) => {
  const { name, logoUrl, primaryColor, buttonTextColor, footerText } = settings;

  const brandName = name?.trim() || PRODUCT_NAME;
  const requester = senderName?.trim() || "<Your Name>";
  const logo = getRenderableLogoUrl(logoUrl) || inkfreeLogo;
  const expiryDate = moment().add(1, "month").format(EXPIRY_FORMAT);
  const copyright = `© ${moment().year()} ${brandName}. All rights reserved.`;
  const footerRight = footerText?.trim() || copyright;

  return (
    <Box sx={styles.previewColumn}>
      <Box sx={styles.previewHeader}>
        <Typography component="h2" sx={styles.previewTitle}>
          Live Email Preview
        </Typography>
        <Typography component="div" sx={styles.previewBadge}>
          SIGNER VIEW
        </Typography>
      </Box>

      <Box sx={styles.emailCard}>
        <Box sx={styles.emailHeader}>
          <Avatar sx={styles.emailAvatar(primaryColor)}>
            {brandName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={styles.emailSenderText}>
            <Typography component="div" sx={styles.emailSender}>
              <Box component="strong" sx={styles.emailStrong}>
                {brandName}
              </Box>{" "}
              <Box component="span" sx={styles.emailSenderMuted}>
                via {PRODUCT_NAME}
              </Box>
            </Typography>
            <Typography component="div" sx={styles.emailRecipient}>
              to me
            </Typography>
          </Box>
        </Box>

        <Box sx={styles.emailBody}>
          <Box component="span">Hi &lt;Recipient Name&gt;,</Box>
          <Box component="span">
            <Box component="strong" sx={styles.emailStrong}>
              {requester}
            </Box>{" "}
            has requested your signature on{" "}
            <Box component="strong" sx={styles.emailStrong}>
              {SAMPLE_DOCUMENT}
            </Box>
            . To review and sign the document, please click the button below:
          </Box>
          <Box
            component="span"
            sx={styles.emailCta(primaryColor, buttonTextColor)}
          >
            Review and Sign
          </Box>
          <Box component="span">
            You have until{" "}
            <Box component="strong" sx={styles.emailStrong}>
              {expiryDate}
            </Box>
            * to sign this document. This email contains a secure link—do not
            share it with others. If you're unwilling to sign, you can either
            delegate the request or decline it.
          </Box>
          <Box component="span" sx={styles.emailFooterText}>
            {`Best,\n${requester}\n*All dates are set to EST.`}
          </Box>
          <Box component="span">
            Having trouble accessing the links in this email? Paste the
            following URL into your web browser:
            <br />
            <Box component="a" href={SAMPLE_URL} sx={styles.emailLink}>
              {SAMPLE_URL}
            </Box>
          </Box>
          <Box component="span" sx={styles.emailFooterText}>
            <Box component="span" sx={{ color: primaryColor }}>
              Have a wonderful day!
            </Box>
            <br />
            {`The ${brandName} Team`}
          </Box>
          <Box sx={styles.emailFooterRow}>
            <Box component="img" src={logo} alt={brandName} sx={styles.emailLogo} />
            <Box component="span" sx={styles.emailLegal}>
              {footerRight}
            </Box>
          </Box>
        </Box>
      </Box>

      <Typography component="div" sx={styles.previewCaption}>
        Updates as you change logo, color, and footer.
      </Typography>
    </Box>
  );
};

export default EmailPreview;
